import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import algosysLogo from '../assets/algosys_logo.png';
import { ROUTES } from '../routes/constants';
import { BRANDING_STATUS } from './brandingPresentation';
import { useBranding } from './portalBrandingState';

// Login, extension connect and the marketing pages own their own document title.
// All other routes share the resolved portal identity rather than the static
// "AlgoSys" title from index.html.
const PAGE_OWNED_TITLES = new Set([
  ROUTES.LOGIN,
  ROUTES.EXTENSION_CONNECT,
  ROUTES.TRADINGVIEW_LANDING,
  ROUTES.TRADINGVIEW_LANDING_THANK_YOU,
]);

const NEUTRAL_TITLE = 'Trading Workspace';

export default function PortalBrowserMetadata() {
  const { pathname } = useLocation();
  const branding = useBranding();

  useEffect(() => {
    if (PAGE_OWNED_TITLES.has(pathname)) return;
    if (branding.status === BRANDING_STATUS.MAIN) {
      document.title = 'AlgoSys';
    } else if (branding.status === BRANDING_STATUS.BRANDED) {
      document.title = branding.businessName || NEUTRAL_TITLE;
    } else {
      // A pending or failed branding lookup must not claim another Operator's
      // identity (or present the AlgoSys title as an Operator's business).
      document.title = NEUTRAL_TITLE;
    }
  }, [pathname, branding.status, branding.businessName]);

  useEffect(() => {
    const icon = document.querySelector('link[rel="icon"]');
    if (!icon) return undefined;

    const originalHref = icon.getAttribute('href');
    const originalType = icon.getAttribute('type');

    // The branding provider has already validated and loaded this PNG as an
    // origin-scoped object URL. Never resolve an arbitrary logo reference/URL.
    const favicon = branding.status === BRANDING_STATUS.MAIN
      ? algosysLogo
      : (branding.status === BRANDING_STATUS.BRANDED
          && typeof branding.logoSrc === 'string'
          && branding.logoSrc.startsWith('blob:')
        ? branding.logoSrc
        : null);

    if (favicon) {
      icon.setAttribute('href', favicon);
      icon.setAttribute('type', 'image/png');
    } else {
      // An unidentified portal or one without a valid custom logo stays
      // neutral, not misbranded with an AlgoSys/another Operator favicon.
      icon.removeAttribute('href');
      icon.removeAttribute('type');
    }

    return () => {
      if (originalHref === null) icon.removeAttribute('href');
      else icon.setAttribute('href', originalHref);
      if (originalType === null) icon.removeAttribute('type');
      else icon.setAttribute('type', originalType);
    };
  }, [branding.status, branding.logoSrc]);

  return null;
}
